import {Component, Inject} from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef
} from '@angular/material/dialog';
import {PageEvent} from '@angular/material/paginator';
import { NotificationService } from '../../../../services/notification-service';
import {
    IPbaUser,
    ProductUserRole,
} from '../../../../types/d3ePolicy';
import { D3eService } from 'src/services/d3e-service';
import { EndpointUserDialogComponent, IEndpointUserDialogOptions, IEndpointUserResult } from './endpoint-user-dialog.component';
import {catchError, concatMap, count, EMPTY, from, mergeMap, switchMap} from 'rxjs';
import { ActionConfirmationDialogComponent,
         ConsoleAction,
         getActionDialogData,
         IActionConfirmationDialogData,
         IActionConfirmationDialogResult
} from '../action-confirmation-dialog/action-confirmation-dialog.component';

export type EndpointUserType = 'pba' | 'fde';
export type IEndpointUser = IPbaUser;

export interface IManageEndpointUserDialogData {
    title: string;
    userType: EndpointUserType;
    users: IEndpointUser[];
    biosSerial: string,
    endpointUuid: string;
    licenseId: number;
    endpointName: string;
    diskSerial: string;
    pwMin: number;
}

@Component({
    selector: 'app-manage-endpoint-user-dialog',
    templateUrl: './manage-endpoint-user-dialog.component.html',
    styleUrls: ['./manage-endpoint-user-dialog.component.scss'],
    standalone: false,
})
export class ManageEndpointUserDialogComponent {
    displayedColumns = [
        'checkbox',
        'username',
        'email',
        'role',
        'status',
    ];

    selectedUsers: IEndpointUser[] = [];

    pageSize = 10;
    pageIndex = 0;

    protected readonly ProductUserRole = ProductUserRole;
    protected readonly ConsoleAction = ConsoleAction;

    constructor(
        private readonly dialog: MatDialog,
        private readonly d3eService: D3eService,
        private notification: NotificationService,
        public readonly dialogRef: MatDialogRef<
            ManageEndpointUserDialogComponent
        >,
        @Inject(MAT_DIALOG_DATA)
        public data: IManageEndpointUserDialogData,
    ) {}

    get pagedUsers(): IEndpointUser[] {
        const start = this.pageIndex * this.pageSize;
        const end = start + this.pageSize;

        return this.data.users.slice(start, end);
    }

    get allSelected(): boolean {
        return (
            this.data.users.length > 0 &&
            this.selectedUsers.length === this.data.users.length
        );
    }

    get someSelected(): boolean {
        return (
            this.selectedUsers.length > 0 &&
            !this.allSelected
        );
    }

    isSelected(user: IEndpointUser): boolean {
        return this.selectedUsers.includes(user);
    }

    toggleUser(user: IEndpointUser, checked: boolean): void {
        if (checked) {
            if (!this.isSelected(user)) {
                this.selectedUsers = [
                    ...this.selectedUsers,
                    user,
                ];
            }

            return;
        }

        this.selectedUsers = this.selectedUsers.filter(
            selectedUser => selectedUser !== user
        );
    }

    toggleSelectAll(checked: boolean): void {
        this.selectedUsers = checked
            ? [...this.data.users]
            : [];
    }

    hasSelectedUserWithPendingAction(): boolean {
    return (
        this.selectedUsers.length === 1 &&
        !this.selectedUsers.every(
            user => user.action === undefined
        )
    );
    }

    hasSelectedUserWithoutPendingAction(): boolean {
    return (
        this.selectedUsers.length === 1 &&
        this.selectedUsers.every(
            user => user.action === undefined
        )
    );
    }

    hasSelectedUsersWithoutPendingAction(): boolean {
    return (
        this.selectedUsers.length >= 1 &&
        this.selectedUsers.every(
            user => user.action === undefined
        )
    );
    }

    handlePageEvent(pageEvent: PageEvent): void {
        this.pageSize = pageEvent.pageSize;
        this.pageIndex = pageEvent.pageIndex;
    }

    private mergeUsersWithPendingActions(baseUsers: any[], pendingUsers: any[]): any[] {
        const seen = new Set<string>();
        const result: any[] = [];

        const baseUserMap = new Map<string, any>();
        for (const user of baseUsers) {
            if (user.username) {
                baseUserMap.set(user.username, { ...user });
            }
        }

        for (const pending of pendingUsers) {
            const username = pending.username;
            if (!username) continue;

            const base = baseUserMap.get(username) ?? {
                username: '',
                email: '',
                role: 0,
                disk_serial: '',
            };

            const merged: any = {
                ...base,
                ...pending,
            };

            const isDeleteAction =
            pending.action === ConsoleAction.REMOVE_PBA_USER.valueOf ||
            pending.action === ConsoleAction.REMOVE_FDE_USER.valueOf;

            if (isDeleteAction) {
                merged.role = base.role;
            }

            if (!seen.has(username)) {
                seen.add(username);
                result.push(merged);
            }
        }

        for (const [username, user] of baseUserMap) {
            if (!seen.has(username)) {
                seen.add(username);
                result.push(user);
            }
        }

        return result;
    }

    private refreshActionData(): void {
        this.d3eService.getActionData(
            [this.data.endpointUuid],
            true
        ).subscribe(actionData => {
            const endpointData = actionData.find(
                data => data.license_id === this.data.licenseId
            );

            if (!endpointData) {
                return;
            }

            const isPBA = this.data.userType === 'pba';
            const baseUsers = isPBA
                ? (endpointData.pba_users ?? [])
                : (endpointData.fde_users ?? []);
            const pendingUsers = isPBA
                ? (endpointData.pending_pba_users ?? [])
                : (endpointData.pending_fde_users ?? []);

            const combinedUsers = this.mergeUsersWithPendingActions(baseUsers, pendingUsers);

            this.data = {
                ...this.data,
                users: combinedUsers,
                pwMin: isPBA
                    ? (endpointData.pba_pw_min ?? this.data.pwMin)
                    : (endpointData.fde_pw_min ?? this.data.pwMin),
            };

            this.selectedUsers = [];

            const maxPageIndex = Math.max(
                Math.ceil(this.data.users.length / this.pageSize) - 1,
                0
            );

            if (this.pageIndex > maxPageIndex) {
                this.pageIndex = maxPageIndex;
            }
        });
    }

    addUser(): void {
        const options: IEndpointUserDialogOptions = {
            userType: this.data.userType,
            mode: 'add',
            endpoints: [
                {
                    licenseId: this.data.licenseId,
                    endpointName: this.data.endpointName,
                    diskSerial: this.data.diskSerial,
                    pwMin: this.data.pwMin,
                },
            ],
        };

        this.dialog.open(
            EndpointUserDialogComponent,
            {data: options}
        ).afterClosed().subscribe(
            (result: IEndpointUserResult) => {
                if (!result) {
                    return;
                }

                const row = result.users[0];

                const user: IEndpointUser = {
                    disk_serial: row.disk_serial,
                    username: row.username,
                    email: row.email,
                    role: row.role,
                    password: row.password,
                };

                const request = this.data.userType === 'pba'
                    ? this.d3eService.addPbaUser(
                        this.data.endpointUuid,
                        this.data.biosSerial,
                        user
                    )
                    : this.d3eService.addFdeUser(
                        this.data.endpointUuid,
                        this.data.biosSerial,
                        user
                    );

                request.subscribe({
                    next: () => {
                        this.refreshActionData();
                    },
                    error: () => {
                        this.notification.showError(`Failed to add ${this.data.userType.toUpperCase()} user`);
                    },
                });
            }
        );
    }

    editUser(): void {
        if (this.selectedUsers.length !== 1) {
            return;
        }

        const selectedUser = this.selectedUsers[0];

        const options: IEndpointUserDialogOptions = {
            userType: this.data.userType,
            mode: 'edit',
            endpoints: [
                {
                    licenseId: this.data.licenseId,
                    endpointName: this.data.endpointName,
                    diskSerial: this.data.diskSerial,
                    pwMin: this.data.pwMin,
                },
            ],
            user: selectedUser,
        };

        this.dialog.open(
            EndpointUserDialogComponent,
            {data: options}
        ).afterClosed().subscribe(
            (result: IEndpointUserResult) => {
                if (!result) {
                    return;
                }

                const row = result.users[0];

                const user: IEndpointUser = {
                    disk_serial: row.disk_serial,
                    username: row.username,
                    email: row.email,
                    role: row.role,
                    password: row.password,
                };

                const request = this.data.userType === 'pba'
                    ? this.d3eService.updatePbaUser(this.data.endpointUuid, user)
                    : this.d3eService.updateFdeUser(this.data.endpointUuid, user);

                request.subscribe({
                    next: () => {
                        this.refreshActionData();
                    },
                    error: () => {
                        this.notification.showError(`Failed to update ${this.data.userType.toUpperCase()} user`);
                    },
                });
            }
        );
    }

    removeUsers(): void {
        if (this.selectedUsers.length === 0) {
            return;
        }

        const action = this.data.userType === 'pba'
            ? ConsoleAction.REMOVE_PBA_USER
            : ConsoleAction.REMOVE_FDE_USER;

        const data = getActionDialogData(
            action,
            this.data.biosSerial,
            '',
        );

        this.dialog.open<
            ActionConfirmationDialogComponent,
            IActionConfirmationDialogData,
            IActionConfirmationDialogResult
        >(ActionConfirmationDialogComponent, {
            data,
        }).afterClosed().pipe(
            switchMap(result => {
                if (!result?.confirmed) {
                    return EMPTY;
                }

                return from(this.selectedUsers).pipe(
                    concatMap(user => 
                        this.data.userType === 'pba'
                            ? this.d3eService.deletePbaUser(this.data.endpointUuid, user).pipe(catchError(() => EMPTY))
                            : this.d3eService.deleteFdeUser(this.data.endpointUuid, user).pipe(catchError(() => EMPTY))
                    ),
                    count()
                );
            })
        ).subscribe({
            next: () => {
                this.refreshActionData();
                this.notification.showSuccess(
                    `Removed ${this.selectedUsers.length} ${this.data.userType.toUpperCase()} user${this.selectedUsers.length !== 1 ? 's' : ''}`
                );
            }
        });
    }

    cancelUser(): void {
        if (this.selectedUsers.length !== 1) {
            return;
        }

        const user = this.selectedUsers[0];

        const action = this.data.userType === 'pba'
            ? ConsoleAction.CANCEL_PBA_USER_ACTION
            : ConsoleAction.CANCEL_FDE_USER_ACTION;

        const data = getActionDialogData(
            action,
            this.data.biosSerial,
            '',
        );

        this.dialog.open<
            ActionConfirmationDialogComponent,
            IActionConfirmationDialogData,
            IActionConfirmationDialogResult
        >(ActionConfirmationDialogComponent, {
            data,
        }).afterClosed().pipe(
            switchMap(result => {
                if (!result?.confirmed) {
                    return EMPTY;
                }

                return this.d3eService.removeUserConsoleAction(this.data.endpointUuid, user, action);
            })
        ).subscribe({
            next: () => {
                this.refreshActionData();
            },
            error: () => {
                this.notification.showError('Failed to cancel action');
            },
        });
    }

    close(): void {
        this.dialogRef.close();
    }
}